import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, CircularProgress, Divider, Paper, Chip,
} from '@mui/material';
import BarChartIcon      from '@mui/icons-material/BarChart';
import DevicesIcon       from '@mui/icons-material/Devices';
import PhoneAndroidIcon  from '@mui/icons-material/PhoneAndroid';
import { getViews }      from '../../pitch/services/pitchService';
import { APPLAB_ACCENT } from '../constants';

function fmt(sec) {
  if (!sec || sec < 1) return '–';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export default function ShareStatsDialog({ open, onClose, share }) {
  const [views,   setViews]   = useState([]);
  const [loading, setLoading] = useState(false);
  const loaded = useRef(false);

  useEffect(() => {
    if (!open || !share?.id || loaded.current) return;
    setLoading(true);
    getViews(share.id)
      .then(v => { setViews(v); loaded.current = true; })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, share?.id]);

  useEffect(() => { loaded.current = false; setViews([]); }, [share?.id]);

  if (!share) return null;

  const total       = views.length;
  const durations   = views.map(v => {
    const a = v.startedAt?.toDate?.();
    const b = v.lastSeen?.toDate?.();
    return a && b ? (b - a) / 1000 : null;
  }).filter(d => d !== null && d > 2);
  const avgDuration = durations.length ? durations.reduce((s, d) => s + d, 0) / durations.length : null;
  const mobile      = views.filter(v => v.device === 'mobil').length;
  const now         = Date.now();
  const week7       = views.filter(v => {
    const d = v.startedAt?.toDate?.();
    return d && (now - d.getTime()) < 7 * 24 * 3600 * 1000;
  }).length;
  const recent = views.slice(0, 10);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BarChartIcon sx={{ color: APPLAB_ACCENT }} />
          <Box>
            <Typography fontWeight={700}>{share.title || 'Delning'}</Typography>
            <Typography variant="caption" color="text.secondary">Visningsstatistik</Typography>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: APPLAB_ACCENT }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 2 }}>
              {[
                { label: 'Totalt visningar', value: total },
                { label: 'Snitt visningstid', value: fmt(avgDuration) },
                { label: 'Senaste 7 dagarna', value: week7 },
              ].map(({ label, value }) => (
                <Paper key={label} variant="outlined" sx={{ p: 1.5, borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={700} sx={{ color: APPLAB_ACCENT }}>{value}</Typography>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Paper>
              ))}
            </Box>

            {total === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2, fontSize: '0.85rem' }}>
                Inga visningar registrerade ännu.
              </Typography>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PhoneAndroidIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                  <Typography variant="body2" color="text.secondary">Mobil: <strong>{mobile}</strong></Typography>
                  <DevicesIcon sx={{ color: 'text.secondary', fontSize: 18, ml: 1 }} />
                  <Typography variant="body2" color="text.secondary">Dator: <strong>{total - mobile}</strong></Typography>
                </Box>

                <Box>
                  <Typography variant="caption" fontWeight={700} color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                    Senaste visningar
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {recent.map(v => {
                      const d = v.startedAt?.toDate?.();
                      const dur = (() => {
                        const a = v.startedAt?.toDate?.();
                        const b = v.lastSeen?.toDate?.();
                        return a && b ? (b - a) / 1000 : null;
                      })();
                      return (
                        <Box key={v.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.75,
                          borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" sx={{ width: 120, color: 'text.secondary', flexShrink: 0 }}>
                            {d ? d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '–'}
                          </Typography>
                          <Typography variant="caption" sx={{ width: 50, flexShrink: 0 }}>{v.device || '–'}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{fmt(dur)}</Typography>
                          {v.completed && (
                            <Chip label="Klar" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.65rem', height: 18, ml: 'auto' }} />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Stäng</Button>
      </DialogActions>
    </Dialog>
  );
}
